import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { motivationColor } from '../../helpers/motivationColors'
import { formatSharePct, NO_VALUE } from '../../helpers/scoreDisplay'
import { findMotivation, patternsForMotivation } from '../../helpers/showcase'
import { useShowcaseMotivationCreatives } from '../../hooks/useShowcaseMotivationCreatives'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsShowcase } from '../../hooks/useUAInsightsShowcase'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { MotivationScoresBySource } from '../../types'
import { MotivationDetailLayout } from '../MotivationDetailLayout/MotivationDetailLayout'

interface GameMotivationDetailViewProps {
  /** Scopes creatives + story patterns to this game. */
  gameId: string
  motivationId: string
  /** The game's per-source motivation scores (already loaded by the page). */
  motivationScores: MotivationScoresBySource
  /** Open a story pattern in the game's Story Patterns tab, lensed by this motivation. */
  onOpenPattern: (patternId: string, lensMotivationId: string) => void
}

/**
 * Game-scoped Motivation detail view — the game-page counterpart to the
 * dashboard's `MotivationDetailView`. Surfaces the story patterns and creatives
 * of a single game that lean on one creative motivation, so the user can drill
 * from the Motivations charts or a Story Pattern's associated-motivation chips
 * into a focused, in-page motivation breakdown (rather than leaving the game).
 */
export const GameMotivationDetailView: FC<GameMotivationDetailViewProps> = ({ gameId, motivationId, motivationScores, onOpenPattern }) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()
  const { motivationById } = useUAInsightsMeta()
  const { data: showcase, isLoading } = useUAInsightsShowcase({ gameId })

  // Game-scoped creatives carrying this motivation, best performing first.
  const { data: creativesData } = useShowcaseMotivationCreatives({ motivationId, gameId })
  const creatives = creativesData?.creatives ?? []

  // Only the patterns this game actually uses (>=1 creative) qualify, mirroring
  // the Story Patterns tab so the two views stay consistent.
  const patterns = useMemo(() => {
    if (!showcase) return []
    const used = showcase.narrativePatterns.filter((p) => p.creativesCount > 0)
    return patternsForMotivation(used, showcase.narrativeMotivations, motivationId)
  }, [showcase, motivationId])

  const stats = showcase ? findMotivation(showcase.motivations, motivationId) : undefined
  const motivation = stats?.motivation ?? motivationById(motivationId)
  const intensity = motivationScores.creativeAds.find((m) => m.motivation.id === motivationId)?.avgIntensity

  if (!motivation) return null
  const color = motivationColor(motivation)

  return (
    <MotivationDetailLayout
      title={motivationLabel(motivation)}
      motivation={motivation}
      color={color}
      stats={[
        { value: stats ? formatSharePct(stats.sharePct) : NO_VALUE, label: t('showcase_stat_share_of_creatives'), color },
        { value: intensity !== undefined ? intensity.toFixed(1) : NO_VALUE, label: t('game_motivation_stat_creative_intensity'), color },
        { value: creativesData?.page.total ?? creatives.length, label: t('showcase_stat_creatives') },
      ]}
      patterns={patterns}
      isLoadingPatterns={isLoading}
      hideTopGames
      onSelectPattern={(patternId) => onOpenPattern(patternId, motivationId)}
      creatives={creatives}
      creativesTotal={creativesData?.page.total}
    />
  )
}
